import { Search } from '../../atoms/Search';
import { cn } from '../../lib/utils';
import { FilterDropdown, type FilterDropdownOption } from '../FilterDropdown';
import { FilterPillGroup, type FilterPillOption } from '../FilterPillGroup';

/**
 * One Sensitivity pill option. Value is the reserved `'all'` sentinel or a
 * catalogued (lowercase) sensitivity label; `label` is the humanized display
 * string and `count` is the faceted tally rendered as `Label (count)`. The app
 * derives these from the envelope's `sensitivityCounts` (server-driven, design
 * v6 §3.3, Decision #13) — the molecule no longer hard-codes the five levels.
 */
export interface SensitivityFilterOption {
  value: string;
  label: string;
  count: number;
}

/** Rows on this page, events in the filtered set, and the distinct files behind them when counted. */
export interface AccessLegitimacyShowing {
  visible: number;
  total: number;
  files?: number;
}

export interface AccessLegitimacyFiltersProps {
  /** Currently-selected sensitivity value (`'all'` or a catalogued label). */
  selectedSensitivity: string;
  /** Fires with the next sensitivity value when a chip is clicked / focused. */
  onSensitivityChange: (next: string) => void;
  /** Server-derived Sensitivity pill options ("All Sensitivities" first, then catalog order). */
  sensitivityOptions: ReadonlyArray<SensitivityFilterOption>;

  /** Controlled (already-debounced upstream) search query value. */
  searchQuery: string;
  /** Fires with the new search query on every keystroke. */
  onSearchChange: (next: string) => void;
  /** Placeholder for the Search atom. */
  searchPlaceholder?: string;

  /** Options for the User-type checkbox dropdown (server-derived). */
  userTypeOptions: ReadonlyArray<FilterDropdownOption>;
  selectedUserTypes: readonly string[];
  onUserTypesChange: (next: readonly string[]) => void;
  userTypeBaseLabel: string;

  /** Options for the File-type checkbox dropdown (server-derived). */
  fileTypeOptions: ReadonlyArray<FilterDropdownOption>;
  selectedFileTypes: readonly string[];
  onFileTypesChange: (next: readonly string[]) => void;
  fileTypeBaseLabel: string;

  /** Accessible name for the sensitivity `role="radiogroup"`. */
  sensitivityRadiogroupAriaLabel?: string;

  /** The counts the header indicator renders. */
  showing: AccessLegitimacyShowing;
  showingTemplate: (args: AccessLegitimacyShowing) => string;

  className?: string;
}

/**
 * AccessLegitimacyFilters — two-row vertical filter rail for the Access
 * Legitimacy widget (design §3.3 / Figma `5:5779`). Fully controlled and fully
 * server-driven: every option list (Sensitivity pills + counts, User-type /
 * File-type dropdowns) is supplied by the caller from the live envelope.
 *
 *  - Row 1: `FilterPillGroup mode='radiogroup'` (Sensitivity options + faceted
 *    counts) + the "N events · M files" indicator.
 *  - Row 2: `Search` + User-type `FilterDropdown` + File-type `FilterDropdown`.
 */
export function AccessLegitimacyFilters({
  selectedSensitivity,
  onSensitivityChange,
  sensitivityOptions,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  userTypeOptions,
  selectedUserTypes,
  onUserTypesChange,
  userTypeBaseLabel,
  fileTypeOptions,
  selectedFileTypes,
  onFileTypesChange,
  fileTypeBaseLabel,
  sensitivityRadiogroupAriaLabel = 'Filter by sensitivity',
  showing,
  showingTemplate,
  className,
}: AccessLegitimacyFiltersProps) {
  const chipOptions: ReadonlyArray<FilterPillOption> = sensitivityOptions.map((opt) => ({
    value: opt.value,
    label: `${opt.label} (${opt.count.toLocaleString('en-US')})`,
  }));

  const userTypeTriggerLabel =
    selectedUserTypes.length === 0
      ? userTypeBaseLabel
      : `${userTypeBaseLabel} (${selectedUserTypes.length})`;

  const fileTypeTriggerLabel =
    selectedFileTypes.length === 0
      ? fileTypeBaseLabel
      : `${fileTypeBaseLabel} (${selectedFileTypes.length})`;

  const indicatorText = showingTemplate(showing);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-row items-center justify-between gap-3">
        <FilterPillGroup
          mode="radiogroup"
          aria-label={sensitivityRadiogroupAriaLabel}
          options={chipOptions}
          value={selectedSensitivity}
          onChange={onSensitivityChange}
        />
        <span aria-live="polite" className="font-outfit text-[13px] leading-[1.3] text-grey-500">
          {indicatorText}
        </span>
      </div>

      <div className="flex flex-row items-center gap-2">
        <Search
          value={searchQuery}
          onChange={onSearchChange}
          {...(searchPlaceholder !== undefined ? { placeholder: searchPlaceholder } : {})}
        />
        <FilterDropdown
          selectionMode="checkbox"
          options={userTypeOptions}
          value={selectedUserTypes}
          onChange={(next) =>
            onUserTypesChange(Array.isArray(next) ? next : ([next] as readonly string[]))
          }
          showSelectAll
          onSelectAll={() => onUserTypesChange(userTypeOptions.map((o) => o.value))}
          showClearSelection
          onClearSelection={() => onUserTypesChange([])}
          width={184}
          triggerLabel={userTypeTriggerLabel}
          triggerTestId="access-legitimacy-filters-user-type-trigger"
        />
        <FilterDropdown
          selectionMode="checkbox"
          options={fileTypeOptions}
          value={selectedFileTypes}
          onChange={(next) =>
            onFileTypesChange(Array.isArray(next) ? next : ([next] as readonly string[]))
          }
          showSearch
          showSelectAll
          onSelectAll={() => onFileTypesChange(fileTypeOptions.map((o) => o.value))}
          showClearSelection
          onClearSelection={() => onFileTypesChange([])}
          width={184}
          triggerLabel={fileTypeTriggerLabel}
          triggerTestId="access-legitimacy-filters-file-type-trigger"
        />
      </div>
    </div>
  );
}

AccessLegitimacyFilters.displayName = 'AccessLegitimacyFilters';
